/**
 * Real Elevation Data Integration
 * Calls backend /api/terrain/elevation endpoint (NOT direct external API)
 * Backend handles OpenTopography API calls securely
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

/**
 * Fetch real elevation data for a route's bounding box
 * For long routes, automatically splits into segments
 * @param {object} startPoint - {lat, lng}
 * @param {object} endPoint - {lat, lng}
 * @param {number} margin - Margin in degrees (default 0.05 = ~5.5km)
 * @returns {Promise<object>} Elevation grid data (merged if segmented)
 */
export const fetchRouteElevation = async (startPoint, endPoint, margin = 0.05) => {
    try {
        // Calculate route distance
        const latDiff = Math.abs(endPoint.lat - startPoint.lat);
        const lngDiff = Math.abs(endPoint.lng - startPoint.lng);
        const routeSize = Math.max(latDiff, lngDiff);
        
        // Determine if route needs segmentation
        // Segment if route >0.8° (~90km) to keep each segment <10M points
        // At 30m resolution: 0.8° + 0.01° margin = 0.82° = ~3,034 x 3,034 = ~9.2M points ✓
        const SEGMENT_SIZE = 0.8; // degrees (~90km)
        
        if (routeSize > SEGMENT_SIZE) {
            console.log(`🔄 Long route detected (${routeSize.toFixed(2)}°) - splitting into segments...`);
            return await fetchRouteElevationSegmented(startPoint, endPoint, SEGMENT_SIZE, margin);
        }
        
        // For shorter routes, fetch normally with adaptive margin
        let adaptiveMargin = margin;
        if (routeSize > 0.5) {
            adaptiveMargin = 0.02; // ~2km for medium routes
        } else if (routeSize > 0.2) {
            adaptiveMargin = 0.03; // ~3km for short routes
        }
        
        const bbox = {
            south: Math.min(startPoint.lat, endPoint.lat) - adaptiveMargin,
            north: Math.max(startPoint.lat, endPoint.lat) + adaptiveMargin,
            west: Math.min(startPoint.lng, endPoint.lng) - adaptiveMargin,
            east: Math.max(startPoint.lng, endPoint.lng) + adaptiveMargin
        };
        
        console.log(`🌍 Fetching elevation data (margin: ${adaptiveMargin}°)...`);
        
        // Call YOUR backend (NOT external API directly!)
        const response = await fetch(`${API_URL}/api/v1/terrain/elevation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
            },
            body: JSON.stringify({ bbox })
        });
        
        if (!response.ok) {
            throw new Error(`Elevation API returned ${response.status}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
            throw new Error(result.error || 'Elevation fetch failed');
        }
        
        const elevationData = result.data;
        
        console.log(`✅ Elevation data received:`, {
            source: elevationData.source,
            cached: elevationData.cached,
            resolution: elevationData.resolution_m,
            gridSize: `${elevationData.header.nrows}x${elevationData.header.ncols}`
        });
        
        return elevationData;
        
    } catch (error) {
        console.error('❌ Failed to fetch elevation data:', error);
        throw error;  // Throw error instead of returning null
    }
};

/**
 * Fetch elevation data for a long route by splitting into segments
 * @param {object} startPoint - {lat, lng}
 * @param {object} endPoint - {lat, lng}
 * @param {number} segmentSize - Size of each segment in degrees
 * @param {number} margin - Margin for each segment
 * @returns {Promise<object>} Merged elevation grid data
 */
async function fetchRouteElevationSegmented(startPoint, endPoint, segmentSize, margin) {
    const latDiff = endPoint.lat - startPoint.lat;
    const lngDiff = endPoint.lng - startPoint.lng;
    const routeSize = Math.max(Math.abs(latDiff), Math.abs(lngDiff));
    
    // Calculate number of segments needed
    const numSegments = Math.ceil(routeSize / segmentSize);
    
    console.log(`📊 Splitting route into ${numSegments} segments of ~${segmentSize}° each...`);
    
    // Create segment points
    const segments = [];
    for (let i = 0; i < numSegments; i++) {
        const t1 = i / numSegments;
        const t2 = (i + 1) / numSegments;
        
        const segStart = {
            lat: startPoint.lat + latDiff * t1,
            lng: startPoint.lng + lngDiff * t1
        };
        
        const segEnd = {
            lat: startPoint.lat + latDiff * t2,
            lng: startPoint.lng + lngDiff * t2
        };
        
        segments.push({ start: segStart, end: segEnd, index: i });
    }
    
    // Fetch elevation data for each segment
    const segmentDataPromises = segments.map(async (segment) => {
        const adaptiveMargin = 0.01; // ~1km margin for segments (reduced to stay under 10M point limit)
        
        const bbox = {
            south: Math.min(segment.start.lat, segment.end.lat) - adaptiveMargin,
            north: Math.max(segment.start.lat, segment.end.lat) + adaptiveMargin,
            west: Math.min(segment.start.lng, segment.end.lng) - adaptiveMargin,
            east: Math.max(segment.start.lng, segment.end.lng) + adaptiveMargin
        };
        
        console.log(`🔍 Fetching segment ${segment.index + 1}/${numSegments}...`);
        
        const response = await fetch(`${API_URL}/api/v1/terrain/elevation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
            },
            body: JSON.stringify({ bbox })
        });
        
        if (!response.ok) {
            throw new Error(`Segment ${segment.index + 1} elevation API returned ${response.status}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
            throw new Error(result.error || `Segment ${segment.index + 1} elevation fetch failed`);
        }
        
        console.log(`✅ Segment ${segment.index + 1}/${numSegments} complete`);
        
        return result.data;
    });
    
    // Fetch all segments in parallel
    const segmentDataArray = await Promise.all(segmentDataPromises);
    
    console.log(`✅ All ${numSegments} segments fetched - merging elevation data...`);
    
    // Merge segment data into a single elevation dataset
    // For simplicity, we'll use the first segment's structure and combine grids
    const mergedData = mergeElevationSegments(segmentDataArray, segments);
    
    console.log(`✅ Elevation data merged successfully`);
    
    return mergedData;
}

/**
 * Merge multiple elevation grid segments into one
 * @param {array} segmentDataArray - Array of elevation data objects
 * @param {array} segments - Array of segment info
 * @returns {object} Merged elevation data
 */
function mergeElevationSegments(segmentDataArray, segments) {
    // For now, return the first segment and store all segments
    // The getElevationAtPoint function will search across all segments
    const merged = {
        ...segmentDataArray[0],
        segments: segmentDataArray, // Store all segments for lookup
        isSegmented: true,
        segmentCount: segmentDataArray.length
    };
    
    return merged;
}

/**
 * Interpolate elevation for a specific coordinate from grid data
 * Handles both single and segmented elevation data
 * @param {object} coord - {lat, lng}
 * @param {object} elevationData - Grid data from backend
 * @returns {number} Elevation in meters
 */
export const getElevationAtPoint = (coord, elevationData) => {
    if (!elevationData) {
        return 0;
    }
    
    // Handle segmented elevation data
    if (elevationData.isSegmented && elevationData.segments) {
        // Try each segment until we find one containing this coordinate
        for (const segment of elevationData.segments) {
            const elevation = getElevationFromGrid(coord, segment);
            if (elevation !== 0) {
                return elevation;
            }
        }
        return 0; // Coordinate not in any segment
    }
    
    // Handle single elevation grid
    return getElevationFromGrid(coord, elevationData);
};

/**
 * Get elevation from a single grid
 * @param {object} coord - {lat, lng}
 * @param {object} gridData - Single elevation grid data
 * @returns {number} Elevation in meters
 */
function getElevationFromGrid(coord, gridData) {
    if (!gridData || !gridData.grid) {
        return 0;
    }
    
    const { header, grid } = gridData;
    
    // Convert lat/lng to grid indices
    const colIndex = Math.floor((coord.lng - header.xllcorner) / header.cellsize);
    const rowIndex = Math.floor((header.yllcorner + (header.nrows * header.cellsize) - coord.lat) / header.cellsize);
    
    // Bounds checking
    if (rowIndex < 0 || rowIndex >= header.nrows || colIndex < 0 || colIndex >= header.ncols) {
        return 0;
    }
    
    // Return elevation (handle nodata values)
    const elevation = grid[rowIndex]?.[colIndex];
    return (elevation === header.nodata_value || elevation === undefined) ? 0 : elevation;
}

/**
 * Add real elevation to route coordinates
 * @param {array} coordinates - Array of {lat, lng} coordinates
 * @param {object} elevationData - Grid data from backend
 * @returns {array} Coordinates with elevation added
 */
export const enrichCoordinatesWithElevation = (coordinates, elevationData) => {
    if (!elevationData) {
        console.warn('⚠️  No elevation data available - using zero elevation');
        return coordinates.map(coord => ({ ...coord, elevation: 0 }));
    }
    
    return coordinates.map(coord => ({
        ...coord,
        elevation: getElevationAtPoint(coord, elevationData)
    }));
};


